import { FormEvent } from 'react';

export const formatManualTimeInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);

  if (!digits) {
    return '';
  }

  if (digits.length === 1) {
    return digits;
  }

  if (digits.length === 2) {
    return `${digits}:`;
  }

  if (digits.length === 3) {
    const twoDigitHour = Number(digits.slice(0, 2));
    if (twoDigitHour > 23) {
      return `${digits[0]}:${digits.slice(1)}`;
    }
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
};

export const isCompleteManualTimeInput = (value: string): boolean =>
  /^\d{2}:\d{2}$/.test(value);

export const parseManualTimeToDate = (
  value: string,
  baseDate?: Date | null,
): Date | null => {
  const formatted = formatManualTimeInput(value);

  if (!isCompleteManualTimeInput(formatted)) {
    return null;
  }

  const hours = Number(formatted.slice(0, 2));
  const minutes = Number(formatted.slice(3, 5));

  if (hours > 23 || minutes > 59) {
    return null;
  }

  const date = baseDate ? new Date(baseDate) : new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const getCursorPositionAfterFormat = (
  formatted: string,
  digitsBeforeCursor: number,
): number => {
  if (digitsBeforeCursor <= 0) {
    return 0;
  }

  if (digitsBeforeCursor <= 2) {
    const hasAutoColon =
      formatted.length === digitsBeforeCursor + 1 && formatted.endsWith(':');

    return hasAutoColon ? digitsBeforeCursor + 1 : digitsBeforeCursor;
  }

  return Math.min(3 + (digitsBeforeCursor - 2), formatted.length);
};

export const applyManualTimeInputFormat = (input: HTMLInputElement): string => {
  const selectionStart = input.selectionStart ?? input.value.length;
  const oldValue = input.value;
  const formatted = formatManualTimeInput(oldValue);

  if (formatted === oldValue) {
    return formatted;
  }

  const digitsBeforeCursor = oldValue
    .slice(0, selectionStart)
    .replace(/\D/g, '').length;

  input.value = formatted;

  const newCursor = getCursorPositionAfterFormat(formatted, digitsBeforeCursor);

  requestAnimationFrame(() => {
    input.setSelectionRange(newCursor, newCursor);
  });

  return formatted;
};

export const handleManualTimeInputChange = (
  event: FormEvent<HTMLInputElement>,
): void => {
  applyManualTimeInputFormat(event.currentTarget);
};
